import { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Save,
  Trash2,
  Edit,
  Package,
  ChevronRight
} from 'lucide-react';
import './ProductsModal.css';

const ProductsModal = ({ isOpen, onClose, supplier, products: initialProducts = [] }) => {
  const [products, setProducts] = useState(initialProducts);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showProductsList, setShowProductsList] = useState(true);
  const [originalProductData, setOriginalProductData] = useState(null);
  const [newProduct, setNewProduct] = useState({
    code: '',
    description: '',
    unit: 'UN',
    baseCost: '',
    includesIva: false,
    ivaAmount: 0,
    deliveryTime: 15
  });

  useEffect(() => {
    setProducts(initialProducts);
  }, [initialProducts]);

  // Ocultar header cuando modal está abierto en móvil
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('suppliers-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('suppliers-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('suppliers-modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAddProduct = () => {
    if (!newProduct.code || !newProduct.description) {
      alert('Por favor complete código y descripción');
      return;
    }

    const productToAdd = {
      ...newProduct,
      baseCost: parseFloat(newProduct.baseCost) || 0,
      ivaAmount: calculateIva(newProduct.baseCost)
    };

    console.log('Producto a guardar:', productToAdd);

    let updatedProducts;

    if (editingIndex !== null) {
      // Editando producto existente
      updatedProducts = [...products];
      updatedProducts[editingIndex] = productToAdd;
      setEditingIndex(null);
    } else {
      // Agregando nuevo producto
      updatedProducts = [...products, productToAdd];
    }

    setProducts(updatedProducts);

    // Reset form
    setNewProduct({
      code: '',
      description: '',
      unit: 'UN',
      baseCost: '',
      includesIva: false,
      ivaAmount: 0,
      deliveryTime: 15
    });
    setEditingIndex(null);
    setOriginalProductData(null);
    setShowProductForm(false);
  };

  const calculateIva = (cost) => {
    const costNum = parseFloat(cost) || 0;
    return Math.round(costNum * 0.19);
  };

  const calculateNeto = (costWithIva) => {
    const costNum = parseFloat(costWithIva) || 0;
    return Math.round(costNum / 1.19);
  };

  const calculateTotal = () => {
    const base = parseFloat(newProduct.baseCost) || 0;
    if (newProduct.includesIva) {
      return base;
    }
    return base + calculateIva(base);
  };

  const handleEditProduct = (index) => {
    const productToEdit = products[index];
    const productData = {
      code: productToEdit.code || '',
      description: productToEdit.description || '',
      unit: productToEdit.unit || 'UN',
      baseCost: productToEdit.baseCost || '',
      includesIva: productToEdit.includesIva || false,
      ivaAmount: productToEdit.ivaAmount || 0,
      deliveryTime: productToEdit.deliveryTime || 15
    };

    setNewProduct(productData);
    setOriginalProductData(productData);
    setEditingIndex(index);
    setShowProductForm(true);
  };

  const handleDeleteProduct = (index) => {
    const updatedProducts = products.filter((_, i) => i !== index);
    setProducts(updatedProducts);
  };

  // Función para verificar si hay cambios en el producto
  const hasProductChanges = () => {
    if (!originalProductData || editingIndex === null) return true; // Para nuevo producto, siempre habilitado

    return Object.keys(newProduct).some(key => {
      return newProduct[key] !== originalProductData[key];
    });
  };


  const units = [
    'UN', 'KG', 'LT', 'MT', 'M2', 'M3', 'GL', 'TON', 'CJ', 'PQ', 'RS', 'BL', 'HR', 'DIA'
  ];

  if (!isOpen) return null;

  return (
    <div className="products-modal-overlay">
      <div className="products-modal">
        {/* Header */}
        <div className="products-modal-header">
          <h2>
            Productos/Servicios - {supplier?.tradeName || supplier?.legalName}
          </h2>
          <button className="modal-close" onClick={() => onClose(products)}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="products-modal-content">
          {/* Vista de Productos */}
          <div className="products-view">
            <div className="products-content">
              {!showProductForm && (
                <button
                  type="button"
                  className="btn-add-product"
                  onClick={() => setShowProductForm(true)}
                >
                  <Plus size={16} />
                  Agregar Nuevo Producto
                </button>
              )}

              {showProductForm && (
                <div className="product-form">
                  <div className="product-form-grid">
                    <div className="form-group">
                      <label>Código/SKU *</label>
                      <input
                        type="text"
                        value={newProduct.code}
                        onChange={(e) => setNewProduct({...newProduct, code: e.target.value})}
                        placeholder="Ej: PRD-001"
                      />
                    </div>
                    <div className="form-group">
                      <label>Descripción *</label>
                      <input
                        type="text"
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        placeholder="Descripción del producto"
                      />
                    </div>
                    <div className="form-group">
                      <label>Unidad</label>
                      <select
                        value={newProduct.unit}
                        onChange={(e) => setNewProduct({...newProduct, unit: e.target.value})}
                      >
                        {units.map(unit => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Costo Base (CLP)</label>
                      <input
                        type="text"
                        value={newProduct.baseCost && !isNaN(newProduct.baseCost) ? parseInt(newProduct.baseCost).toLocaleString('es-CL') : ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          console.log('Costo base ingresado:', value, 'Tipo:', typeof value);
                          setNewProduct(prevProduct => {
                            console.log('Estado anterior:', prevProduct);
                            const newState = {
                              ...prevProduct,
                              baseCost: value,
                              ivaAmount: calculateIva(value)
                            };
                            console.log('Nuevo estado:', newState);
                            return newState;
                          });
                        }}
                        placeholder="1.000.000"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tiempo Entrega Aproximado (días)</label>
                      <input
                        type="number"
                        value={newProduct.deliveryTime}
                        onChange={(e) => setNewProduct({...newProduct, deliveryTime: e.target.value})}
                        placeholder="15"
                      />
                    </div>
                  </div>

                  <div className="iva-section">
                    <div className="iva-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={newProduct.includesIva}
                          onChange={(e) => {
                            const includesIva = e.target.checked;
                            setNewProduct({
                              ...newProduct,
                              includesIva,
                              ivaAmount: calculateIva(newProduct.baseCost)
                            });
                          }}
                        />
                        <span>Precio incluye IVA (19%)</span>
                      </label>
                    </div>
                  </div>

                  {newProduct.baseCost && (
                    <div className="iva-details-section">
                      {(() => {
                        console.log('Renderizando desglose. baseCost:', newProduct.baseCost);
                        const baseCostValue = parseFloat(newProduct.baseCost) || 0;
                      console.log('baseCostValue parseado:', baseCostValue);
                      const ivaAmount = Math.round(baseCostValue * 0.19);
                      const netoValue = Math.round(baseCostValue / 1.19);

                      if (newProduct.includesIva) {
                        return (
                          <>
                            <div className="price-breakdown">
                              <span>Valor Neto:</span>
                              <strong>${netoValue.toLocaleString('es-CL')}</strong>
                            </div>
                            <div className="price-breakdown">
                              <span>IVA incluido (19%):</span>
                              <strong>${(baseCostValue - netoValue).toLocaleString('es-CL')}</strong>
                            </div>
                            <div className="price-breakdown total-row">
                              <span><strong>Precio Total (IVA incluido):</strong></span>
                              <strong className="total-price">${baseCostValue.toLocaleString('es-CL')}</strong>
                            </div>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <div className="price-breakdown">
                              <span>Valor Neto:</span>
                              <strong>${baseCostValue.toLocaleString('es-CL')}</strong>
                            </div>
                            <div className="price-breakdown">
                              <span>IVA (19%):</span>
                              <strong>${ivaAmount.toLocaleString('es-CL')}</strong>
                            </div>
                            <div className="price-breakdown total-row">
                              <span><strong>Precio Total (con IVA):</strong></span>
                              <strong className="total-price">${(baseCostValue + ivaAmount).toLocaleString('es-CL')}</strong>
                            </div>
                          </>
                        );
                      }
                      })()}
                    </div>
                  )}


                  <div className="product-form-actions">
                    <button
                      type="button"
                      className="btn-cancel-product"
                      onClick={() => {
                        setShowProductForm(false);
                        setEditingIndex(null);
                        setOriginalProductData(null);
                        setNewProduct({
                          code: '',
                          description: '',
                          unit: 'UN',
                          baseCost: '',
                          includesIva: false,
                          ivaAmount: 0,
                          deliveryTime: 15
                        });
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn-save-product"
                      onClick={handleAddProduct}
                      disabled={editingIndex !== null && !hasProductChanges()}
                    >
                      {editingIndex !== null ? 'Actualizar Producto' : 'Guardar Producto'}
                    </button>
                  </div>
                </div>
              )}

              {!showProductForm && products.length > 0 ? (
                <div className="products-list">
                  {products.map((product, index) => (
                    <div key={index} className="product-card">
                      <div className="product-card-info">
                        <div className="product-card-name">
                          {product.code} - {product.description}
                        </div>
                        <div className="product-card-details">
                          Unidad: {product.unit} | Tiempo entrega: {product.deliveryTime} días
                        </div>
                        <div className="product-card-pricing">
                          {product.includesIva ? (
                            <>
                              <span>Neto: ${calculateNeto(product.baseCost).toLocaleString('es-CL')}</span>
                              <span>IVA incluido: ${(product.baseCost - calculateNeto(product.baseCost)).toLocaleString('es-CL')}</span>
                              <span>Total: ${parseInt(product.baseCost || 0).toLocaleString('es-CL')}</span>
                            </>
                          ) : (
                            <>
                              <span>Neto: ${parseInt(product.baseCost || 0).toLocaleString('es-CL')}</span>
                              <span>IVA: ${parseInt(product.ivaAmount || 0).toLocaleString('es-CL')}</span>
                              <span>Total: ${(parseInt(product.baseCost || 0) + parseInt(product.ivaAmount || 0)).toLocaleString('es-CL')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="product-card-actions">
                        <button
                          type="button"
                          className="btn-edit-product"
                          onClick={() => handleEditProduct(index)}
                          title="Editar producto"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-delete-product"
                          onClick={() => handleDeleteProduct(index)}
                          title="Eliminar producto"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !showProductForm ? (
                <div className="no-products">
                  <Package size={48} />
                  <p>No hay productos agregados</p>
                  <p className="text-muted">Agregue productos para este proveedor</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer - only show when not editing/adding */}
        {!showProductForm && (
          <div className="products-modal-footer">
            <button
              type="button"
              className="btn-primary"
              onClick={() => onClose(products)}
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsModal;